"use client";

import { JobTermsSummary } from "@/app/_components/marketplace/JobTermsSummary";
import { formatJobDates, formatJobPay, jobHasEnded, vacancyFreshness } from "@/lib/marketplace/job-terms";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  ROLES_TREE,
  LOCATIONS_TREE,
  LISTING_PERKS,
  QUALIFICATIONS_TREE,
  findLabelById,
  hasEuLocations,
} from "@/lib/taxonomy";
import { SEASON_TYPES } from "@/lib/seasons";
import {
  registerInterest,
  saveListing,
  withdrawInterest,
  unsaveListing,
  sendListingMessage,
  type ListingWithCompany,
} from "@/app/actions/listings";
import type { UserRole } from "@/lib/auth";
import type { DestinationBridge } from "@/lib/destinations/bridge";
import {
  buildMatchExplanations,
  deriveCandidateJobState,
  type PlacementStatus,
} from "@/lib/marketplace/job-state";
import { SharePopover } from "@/app/components/SharePopover";
import { VerifiedBadge } from "@/app/components/VerifiedBadge";
import { JobAlertSignup } from "@/app/components/JobAlertSignup";
import { DestinationBridgeSection } from "@/app/_components/destinations/DestinationBridgeSection";
import { CompanionWorkPrompt } from "@/app/_components/account/CompanionWorkPrompt";
import { trackEvent } from "@/lib/analytics";

import { EligibilitySummary } from "@/app/_components/marketplace/EligibilitySummary";
import { evaluateEligibility, eligibilityMessage, type EligibilityProfile } from "@/lib/marketplace/eligibility";

// hasEuLocations imported from @/lib/taxonomy

function candidateHasEuWorkRights(
  rightToWork: { region: string; status: string }[] | null
): boolean {
  if (!rightToWork) return false;
  return rightToWork.some(
    (r) => (r.region === "EU / Schengen" || r.region === "Switzerland") && r.status === "yes"
  );
}

// ═══ TYPES ═══

interface CandidateWindow {
  id: string;
  season_label: string;
  available_from: string;
  available_to: string;
  positions: string[];
  preferred_locations: string[];
  status: string;
}

// ═══ HELPERS ═══

function relativeTime(dateStr: string): string {
  const days = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000)
  );
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? "s" : ""} ago`;
}

function getSeasonBadgeStyle(displayGroup: string): string {
  switch (displayGroup) {
    case "snow":
      return "bg-[#E8EBF0] text-[#1B2A4A]";
    case "sun":
      return "bg-[#E8F7FE] text-[#0EA5E9]";
    case "festival":
      return "bg-[#FDF0EB] text-[#E8835C]";
    default:
      return "bg-[#F0F0F0] text-text-body";
  }
}

/** Score a window's match quality against a listing (for sorting) */
function scoreWindow(
  w: CandidateWindow,
  listing: ListingWithCompany
): number {
  let score = 0;

  // Date overlap check
  if (listing.start_date && listing.end_date) {
    const lStart = new Date(listing.start_date);
    const lEnd = new Date(listing.end_date);
    const wStart = new Date(w.available_from);
    const wEnd = new Date(w.available_to);
    if (wStart < lEnd && lStart < wEnd) score += 10;
  } else {
    score += 5; // No dates on listing = partial match
  }

  // Role overlap
  if (w.positions.some((p) => listing.roles.includes(p))) score += 5;

  // Location overlap
  if (w.preferred_locations.some((l) => listing.locations.includes(l)))
    score += 3;

  return score;
}

// ═══ WINDOW PICKER MODAL ═══

function WindowPickerModal({
  windows,
  title,
  description,
  onSelect,
  onClose,
}: {
  windows: CandidateWindow[];
  title: string;
  description: string;
  onSelect: (windowId: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[4px]" />
      <div className="relative z-10 bg-white rounded-[16px] p-5 sm:p-7 w-full max-w-[480px] shadow-[0_24px_80px_rgba(0,0,0,0.15)] animate-[scaleIn_0.25s_ease-out]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
        >
          &times;
        </button>
        <h3 className="font-serif text-xl mb-1">{title}</h3>
        <p className="text-[13px] text-text-muted mb-5">{description}</p>
        <div className="flex flex-col gap-2">
          {windows.map((w) => {
            const roleLabels = w.positions
              .map((p) => findLabelById(ROLES_TREE, p))
              .filter(Boolean)
              .slice(0, 3);

            return (
              <button
                key={w.id}
                onClick={() => onSelect(w.id)}
                className="w-full px-4 py-3 rounded-[10px] text-left font-sans border-[1.5px] transition-all bg-warm-bg border-border-light cursor-pointer hover:border-gold hover:bg-gold/[0.03]"
              >
                <span className="text-[14px] font-semibold text-charcoal block mb-1">
                  {w.season_label}
                </span>
                <p className="text-[12px] text-text-muted mb-1.5">
                  {new Date(w.available_from).toLocaleDateString("en-GB", {
                    month: "short",
                    year: "numeric",
                  })}{" "}
                  &ndash;{" "}
                  {new Date(w.available_to).toLocaleDateString("en-GB", {
                    month: "short",
                    year: "numeric",
                  })}
                </p>
                {roleLabels.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {roleLabels.map((r) => (
                      <span
                        key={r}
                        className="bg-gold/[0.08] text-gold px-2 py-0.5 rounded-[5px] text-[10px] font-medium"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

export function ListingDetailContent({
  listing,
  userRole,
  isOwner,
  candidateWindows,
  existingInterests,
  existingConversations,
  isSaved: initialIsSaved = false,
  candidateEligibilityProfile = null,
  candidateRightToWork = null,
  relatedCourses = [],
  destinationBridge = null,
  showCompanionPrompt = false,
}: {
  listing: ListingWithCompany;
  userRole?: UserRole;
  isOwner: boolean;
  candidateWindows: CandidateWindow[];
  existingInterests: { availability_window_id: string }[];
  existingConversations: {
    id: string;
    availability_window_id: string | null;
    placement_status: PlacementStatus;
  }[];
  isSaved?: boolean;
  candidateEligibilityProfile?: EligibilityProfile | null;
  candidateRightToWork?: { region: string; status: string }[] | null;
  relatedCourses?: { id: string; title: string; provider_name: string; qualifications: string[]; price_from: number | null; duration: string | null }[];
  destinationBridge?: DestinationBridge | null;
  showCompanionPrompt?: boolean;
}) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [showWindowPicker, setShowWindowPicker] = useState(false);
  const [windowPickerAction, setWindowPickerAction] = useState<"interest" | "message">("interest");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null);
  const [messageContent, setMessageContent] = useState("");
  const [euBannerDismissed, setEuBannerDismissed] = useState(false);
  const [interestRegistered, setInterestRegistered] = useState(
    existingInterests.length > 0
  );
  const [isSaved, setIsSaved] = useState(initialIsSaved);
  const [saveLoading, setSaveLoading] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [inlineCtaVisible, setInlineCtaVisible] = useState(true);
  const inlineCtaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackEvent("listing_viewed", {
      listing_id: listing.id,
      season_type: listing.season_type,
      role_count: listing.roles.length,
      location_count: listing.locations.length,
      viewer_role: userRole ?? "anonymous",
      is_owner: isOwner,
    });
  }, [
    isOwner,
    listing.id,
    listing.locations.length,
    listing.roles.length,
    listing.season_type,
    userRole,
  ]);

  // Hide sticky bottom CTA while inline CTA is visible
  useEffect(() => {
    const el = inlineCtaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInlineCtaVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const seasonType = SEASON_TYPES.find(
    (st) => st.id === listing.season_type
  );
  const company = listing.company;
  const roleLabels = listing.roles
    .map((r) => findLabelById(ROLES_TREE, r))
    .filter(Boolean);
  const locationLabels = listing.locations
    .map((l) => findLabelById(LOCATIONS_TREE, l))
    .filter(Boolean);
  const initials = company.name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const seasonAccent = seasonType
    ? { snow: "#1B2A4A", sun: "#0EA5E9", festival: "#E8835C" }[seasonType.displayGroup] ?? "#1B2840"
    : "#1B2840";
  const accentColor = company.brand_color || seasonAccent;

  // Sort windows by match quality
  const sortedWindows = [...candidateWindows].sort(
    (a, b) => scoreWindow(b, listing) - scoreWindow(a, listing)
  );

  // Check if there's an existing conversation for this listing
  const existingConv = existingConversations[0];
  const currentState = deriveCandidateJobState({
    saved: isSaved,
    interested: interestRegistered,
    hasConversation: messageSent || !!existingConv,
    isShortlisted: false,
    placementStatus: existingConv?.placement_status ?? null,
    listingStatus: listing.status,
  });
  const matchReasons =
    userRole === "candidate"
      ? buildMatchExplanations({
          listing: {
            roles: listing.roles,
            locations: listing.locations,
            startDate: listing.start_date,
            endDate: listing.end_date,
          },
          candidateWindows: candidateWindows.map((window) => ({
            positions: window.positions,
            preferredLocations: window.preferred_locations,
            availableFrom: window.available_from,
            availableTo: window.available_to,
          })),
          candidateRightToWork:
            candidateRightToWork as
              | { region: string; status: "yes" | "sponsorship" }[]
              | null,
          labelLookup: (id) =>
            findLabelById(ROLES_TREE, id) ??
            findLabelById(LOCATIONS_TREE, id) ??
            id,
        })
      : [];

  const eligibility = evaluateEligibility(listing.eligibility_requirements, candidateEligibilityProfile, listing.start_date);

  // ─── Interest Mode Handler ───
  async function handleInterest(windowId?: string) {
    setError(null);
    const eligibilityError = eligibilityMessage(eligibility);
    if (eligibilityError) { setError(eligibilityError); document.getElementById("job-requirements")?.scrollIntoView({ behavior: "smooth" }); return; }

    if (candidateWindows.length === 0) {
      router.push("/profile?setup=work#availability");
      return;
    }

    if (!windowId && candidateWindows.length > 1) {
      setWindowPickerAction("interest");
      setShowWindowPicker(true);
      return;
    }

    const wId = windowId || candidateWindows[0].id;
    setLoading(true);
    const result = await registerInterest(listing.id, wId);
    if (result.error) {
      setError(result.error);
    } else {
      setInterestRegistered(true);
    }
    setLoading(false);
  }

  async function handleWithdraw() {
    setLoading(true);
    setError(null);
    // Withdraw all interests for this listing
    for (const interest of existingInterests) {
      await withdrawInterest(listing.id, interest.availability_window_id);
    }
    setInterestRegistered(false);
    setLoading(false);
  }

  async function handleSaveToggle() {
    if (userRole !== "candidate") {
      router.push("/join");
      return;
    }

    setSaveLoading(true);
    const nextSaved = !isSaved;
    setIsSaved(nextSaved);
    const result = nextSaved
      ? await saveListing(listing.id)
      : await unsaveListing(listing.id);
    if (result.error) setIsSaved(!nextSaved);
    setSaveLoading(false);
  }

  // ─── Message Mode Handler ───
  function handleStartMessage(windowId?: string) {
    setError(null);
    const eligibilityError = eligibilityMessage(eligibility);
    if (eligibilityError) { setError(eligibilityError); document.getElementById("job-requirements")?.scrollIntoView({ behavior: "smooth" }); return; }

    if (candidateWindows.length === 0) {
      router.push("/profile?setup=work#availability");
      return;
    }

    if (!windowId && candidateWindows.length > 1) {
      setWindowPickerAction("message");
      setShowWindowPicker(true);
      return;
    }

    const wId = windowId || candidateWindows[0].id;
    setSelectedWindowId(wId);
    setShowMessageModal(true);
  }

  async function handleSendMessage() {
    if (!selectedWindowId || !messageContent.trim()) return;
    setLoading(true);
    setError(null);

    const result = await sendListingMessage(
      listing.id,
      messageContent.trim(),
      selectedWindowId,
      "job_detail"
    );

    if (result.error) {
      if (result.conversationId) {
        router.push(`/inbox/${result.conversationId}`);
      } else {
        setError(result.error);
      }
    } else {
      setMessageSent(true);
      setShowMessageModal(false);
    }
    setLoading(false);
  }

  function onWindowSelected(windowId: string) {
    setShowWindowPicker(false);
    if (windowPickerAction === "interest") {
      handleInterest(windowId);
    } else {
      handleStartMessage(windowId);
    }
  }

  const visibleLocationLabels = showAllLocations
    ? locationLabels
    : locationLabels.slice(0, 6);
  const hiddenLocationCount = locationLabels.length - 6;

  return (
    <div className="max-w-[720px] mx-auto px-5 pb-28 pt-2">
      {/* Back link */}
      <button
        onClick={() => router.back()}
        className="text-[14px] text-text-muted bg-transparent border-none cursor-pointer p-0 hover:text-navy transition-colors inline-flex items-center gap-1.5 mt-4 mb-4 font-sans"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 12L6 8L10 4" />
        </svg>
        Back to Jobs
      </button>

      {/* Status badges */}
      {jobHasEnded(listing) && listing.status === "active" && <p role="status" className="mb-4 rounded-md bg-warm-bg p-4 text-sm text-navy">{vacancyFreshness(listing)}. Check updated dates with the employer before making plans.</p>}
      {listing.status === "fulfilled" && (
        <div className="bg-success/[0.06] border border-success/20 rounded-[14px] px-5 py-4 mb-4">
          <p className="text-[14px] font-medium text-[#047857]">
            Position Filled
          </p>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-[18px] border border-border-light shadow-sm overflow-hidden mb-4">
        <div className="p-6 sm:p-7">
          {/* Company row */}
          <div className="flex items-center gap-3 mb-4">
            {company.logo_url ? (
              <Image
                src={company.logo_url}
                alt=""
                width={44}
                height={44}
                sizes="44px"
                className="w-11 h-11 rounded-[12px] object-cover"
              />
            ) : (
              <div
                className="w-11 h-11 rounded-[12px] flex items-center justify-center text-white text-sm font-bold"
                style={{ background: accentColor }}
              >
                {initials}
              </div>
            )}
            <div>
              <span className="flex items-center gap-1.5">
                <Link
                  href={company.slug ? `/company/${company.slug}` : "#"}
                  className="text-[14px] font-semibold text-navy no-underline hover:text-gold transition-colors"
                >
                  {company.name}
                </Link>
                {company.verified && <VerifiedBadge />}
              </span>
              {company.location && (
                <p className="text-[12px] text-text-muted">{company.location}</p>
              )}
            </div>
          </div>

          {/* Title */}
          <h1 className="font-serif text-[26px] sm:text-[32px] font-bold text-navy mb-3 leading-[1.08] tracking-[-0.01em]">
            {listing.title}
          </h1>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            {seasonType && (
              <span
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold ${getSeasonBadgeStyle(seasonType.displayGroup)}`}
              >
                {seasonType.label}
              </span>
            )}
            {listing.response_mode === "message" ? (
              <span className="px-3.5 py-1.5 rounded-full bg-success/[0.08] text-[#047857] text-[12px] font-semibold">
                Accepts messages
              </span>
            ) : (
              <span className="px-3.5 py-1.5 rounded-full bg-navy/[0.06] text-navy text-[12px] font-semibold">
                Express interest
              </span>
            )}
          </div>

          {/* Key details */}
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex items-center gap-2 text-[14px] text-text-body">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted shrink-0"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {formatJobDates(listing)}
            </div>
            <div className="flex items-center gap-2 text-[14px] text-text-body">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted shrink-0"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                {formatJobPay(listing.job_terms)}
            </div>
          </div>

          {/* Interest count */}
          {listing.interest_count > 0 && (
            <p className="text-[13px] text-text-muted mb-4">
              {listing.interest_count} candidate{listing.interest_count !== 1 ? "s" : ""} interested
            </p>
          )}

          {/* Posted date + Share */}
          <div className="flex items-center justify-between">
            <p className="text-[12px] text-text-muted">
              Posted {relativeTime(listing.created_at)}
            </p>
            <div className="flex items-center gap-2">
              {userRole !== "employer" && listing.status === "active" && !jobHasEnded(listing) && (
                <button
                  type="button"
                  onClick={handleSaveToggle}
                  disabled={saveLoading}
                  className={`min-h-[44px] px-4 py-2 rounded-sm text-[13px] font-semibold border-[1.5px] cursor-pointer font-sans transition-all disabled:opacity-50 ${
                    isSaved
                      ? "bg-gold/[0.08] border-gold/30 text-gold"
                      : "bg-white border-border-light text-navy hover:border-gold/40"
                  }`}
                >
                  {saveLoading ? "Saving..." : isSaved ? "Saved" : "Save job"}
                </button>
              )}
              <SharePopover
                url={`https://peakwave.co/jobs/${listing.id}`}
                title={listing.title}
              />
            </div>
          </div>

          {userRole === "candidate" && (
            <div className="mt-5 rounded-[12px] bg-warm-bg border border-border-light px-4 py-3">
              <p className="text-[12px] font-semibold text-navy">
                Job status: {currentState.label}
              </p>
              <p className="text-[12px] text-text-muted mt-0.5">
                {currentState.helper}
              </p>
            </div>
          )}

          {(listing.status !== "active" || jobHasEnded(listing)) && existingConv && <Link href={`/inbox/${existingConv.id}`} className="mt-4 inline-flex min-h-11 items-center text-sm font-semibold text-teal">View existing conversation</Link>}

          {/* ─── CTA (inline in header card) ─── */}
          {listing.status === "active" && !jobHasEnded(listing) && (
            <div ref={inlineCtaRef} className="mt-6 pt-6 border-t border-border-light">
              {/* Owner view */}
              {isOwner && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[14px] font-medium text-charcoal mb-1">
                      {listing.interest_count} candidate{listing.interest_count !== 1 ? "s" : ""} interested
                    </p>
                    <p className="text-[13px] text-text-muted">
                      View and manage interested candidates.
                    </p>
                  </div>
                  <Link
                    href={`/employer/listings/${listing.id}`}
                    className="px-5 py-2.5 rounded-sm text-[14px] font-semibold bg-navy text-white no-underline shrink-0 hover:bg-navy-hover transition-colors"
                  >
                    View Candidates
                  </Link>
                </div>
              )}

              {/* Not logged in */}
              {!userRole && (
                <div className="text-center">
                  <p className="text-[14px] text-text-muted mb-3">
                    Sign up to express your interest. 100% free.
                  </p>
                  <Link
                    href={`/signup?intent=work&next=${encodeURIComponent(`/jobs/${listing.id}`)}`}
                    className="inline-block px-8 py-3 rounded-sm text-[15px] font-semibold bg-gold text-white no-underline hover:bg-gold-light transition-colors"
                  >
                    Sign Up to Express Interest
                  </Link>
                </div>
              )}

              {/* Candidate: already sent message */}
              {userRole === "candidate" && (messageSent || existingConv) && (
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    {messageSent ? "Message sent" : "Interest registered"}
                  </span>
                  {existingConv && (
                    <Link
                      href={`/inbox/${existingConv.id}`}
                      className="text-gold text-[14px] font-semibold no-underline hover:text-gold-light"
                    >
                      View Conversation
                    </Link>
                  )}
                </div>
              )}

              {/* Candidate: Interest mode */}
              {userRole === "candidate" &&
                !isOwner &&
                !existingConv &&
                !messageSent &&
                listing.response_mode === "interest" && (
                  <>
                    {interestRegistered ? (
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-success">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          Interest Registered
                        </span>
                        <button
                          onClick={handleWithdraw}
                          disabled={loading}
                          className="text-[13px] text-text-muted bg-transparent border-none cursor-pointer font-sans underline hover:text-error transition-colors"
                        >
                          Withdraw
                        </button>
                      </div>
                    ) : candidateWindows.length === 0 ? (
                      <div className="text-center">
                        <p className="text-[14px] text-text-muted mb-3">
                          Set up your work profile to express interest.
                        </p>
                        <Link
                          href="/profile?setup=work#availability"
                          className="inline-block px-8 py-3 rounded-sm text-[15px] font-semibold bg-gold text-white no-underline hover:bg-gold-light transition-colors"
                        >
                          Set up for work
                        </Link>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleInterest()}
                        disabled={loading}
                        className="w-full py-3 rounded-sm text-[15px] font-semibold bg-gold text-white border-none cursor-pointer font-sans hover:bg-gold-light transition-colors disabled:opacity-50"
                      >
                        {loading ? "Registering..." : "I\u2019m Interested"}
                      </button>
                    )}
                  </>
                )}

              {/* Candidate: Message mode */}
              {userRole === "candidate" &&
                !isOwner &&
                !existingConv &&
                !messageSent &&
                listing.response_mode === "message" && (
                  <>
                    {candidateWindows.length === 0 ? (
                      <div className="text-center">
                        <p className="text-[14px] text-text-muted mb-3">
                          Set up your work profile to contact this employer.
                        </p>
                        <Link
                          href="/profile?setup=work#availability"
                          className="inline-block px-8 py-3 rounded-sm text-[15px] font-semibold bg-gold text-white no-underline hover:bg-gold-light transition-colors"
                        >
                          Set up for work
                        </Link>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleStartMessage()}
                          disabled={loading}
                          className="flex-1 py-3 rounded-sm text-[15px] font-semibold bg-gold text-white border-none cursor-pointer font-sans hover:bg-gold-light transition-colors disabled:opacity-50"
                        >
                          Send a Message
                        </button>
                        {interestRegistered ? (
                          <span className="inline-flex items-center gap-1.5 px-4 py-3 rounded-sm text-[13px] font-semibold text-success shrink-0">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            Interested
                          </span>
                        ) : (
                          <button
                            onClick={() => handleInterest()}
                            disabled={loading}
                            className="px-5 py-3 rounded-sm text-[14px] font-semibold text-navy bg-transparent border-[1.5px] border-navy/18 cursor-pointer font-sans transition-all hover:border-navy hover:bg-navy/[0.03] disabled:opacity-50 shrink-0"
                          >
                            I&apos;m Interested
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}

              {/* Employer (not owner) */}
              {userRole === "employer" && !isOwner && (
                <p className="text-[14px] text-text-muted">
                  This listing was posted by {company.name}.
                </p>
              )}

              {error && (
                <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-2.5 mt-3 text-[13px] font-medium text-center">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {matchReasons.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-6 sm:p-7 mb-4">
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-3">
            Why this might fit
          </h2>
          <ul className="space-y-2">
            {matchReasons.map((reason) => (
              <li key={reason} className="flex gap-2 text-[14px] leading-[1.6] text-[#5C5649]">
                <span className="text-gold mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Description */}
      <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-6 sm:p-7 mb-4">
        <div id="job-requirements" className="scroll-mt-24"><EligibilitySummary requirements={listing.eligibility_requirements} profile={candidateEligibilityProfile} startDate={listing.start_date} candidate={userRole === "candidate"} /></div>
        <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-3">
          About this role
        </h2>
        <p className="text-[14px] leading-[1.7] text-[#5C5649] m-0 whitespace-pre-line">
          {listing.description}
        </p>
      </div>

      {/* Roles */}
      {roleLabels.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-6 sm:p-7 mb-4">
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-4">
            Roles
          </h2>
          <div className="flex flex-wrap gap-2">
            {roleLabels.map((label) => (
              <span
                key={label}
                className="px-3.5 py-1.5 rounded-full bg-navy text-white text-[13px] font-medium"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Locations */}
      {locationLabels.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-6 sm:p-7 mb-4">
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-4">
            Locations
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {visibleLocationLabels.map((label) => (
              <span
                key={label}
                className="px-3 py-1.5 rounded-full bg-gold/[0.08] text-gold text-[12px] font-medium"
              >
                {label}
              </span>
            ))}
          </div>
          {hiddenLocationCount > 0 && (
            <button
              onClick={() => setShowAllLocations(!showAllLocations)}
              className="mt-2.5 text-[13px] font-medium text-gold bg-transparent border-none cursor-pointer p-0 font-sans hover:text-gold-light transition-colors"
            >
              {showAllLocations
                ? "Show fewer"
                : `Show all ${locationLabels.length} locations`}
            </button>
          )}
        </div>
      )}

      {destinationBridge ? (
        <>
          {showCompanionPrompt ? (
            <div className="mx-auto mb-4 max-w-[1100px] px-5 md:px-12">
              <CompanionWorkPrompt
                bridge={destinationBridge}
                context="listing"
              />
            </div>
          ) : null}
          <DestinationBridgeSection
            bridge={destinationBridge}
            showConditionLink
            heading={`${destinationBridge.name} place context`}
            intro={destinationBridge.contextSummary}
            compact
          />
        </>
      ) : null}

      <JobTermsSummary listing={listing} />

      {/* Perks */}
      {listing.perks.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-6 sm:p-7 mb-4">
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-4">
            Employer benefit tags
          </h2>
          <div className="flex flex-wrap gap-2">
            {listing.perks.map((perkId) => {
              const perk = LISTING_PERKS.find((p) => p.id === perkId);
              if (!perk) return null;
              return (
                <span
                  key={perkId}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-success/[0.06] text-[#047857] text-[13px] font-medium"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
                  {perk.label}
                </span>
              );
            })}
          </div>
        </div>
      )}


      {/* EU Work Rights Banner */}
      {!isOwner &&
        !euBannerDismissed &&
        hasEuLocations(listing.locations) &&
        (userRole !== "candidate" || !candidateHasEuWorkRights(candidateRightToWork)) &&
        userRole !== "employer" && (
        <div className="bg-[#EFF6FF] rounded-[18px] border border-[#BFDBFE] p-6 mb-4 relative">
          <button
            onClick={() => setEuBannerDismissed(true)}
            className="absolute top-3 right-3 w-6 h-6 rounded-full bg-transparent border-none cursor-pointer text-[#64748B] hover:text-[#334155] transition-colors flex items-center justify-center text-sm"
            aria-label="Dismiss"
          >
            &times;
          </button>
          <div className="flex gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <div>
              <h3 className="text-[14px] font-semibold text-[#1E3A5F] m-0 mb-1.5">Working in the EU</h3>
              <p className="text-[13px] text-[#475569] leading-relaxed m-0">
                If you&apos;re a UK passport holder, you may need a work permit, visa, or TIE (Tarjeta de Identidad de Extranjero) to work in the EU. Requirements vary by country. Check with the employer about visa sponsorship and local requirements before applying.
              </p>
              {listing.perks.includes("visa-sponsorship") && (
                <p className="text-[13px] text-[#047857] font-medium mt-2 mb-0 flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  This employer has indicated they offer visa sponsorship.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Related Courses */}
      {relatedCourses.length > 0 && !isOwner && (
        <div className="bg-white border border-border-light shadow-sm rounded-[18px] p-6 sm:p-7 mb-4">
          <h3 className="font-serif text-[18px] font-bold text-navy mb-1">Get qualified for this role</h3>
          <p className="text-[13px] text-text-muted mb-3">
            Courses that prepare you for roles in this listing
          </p>
          <div className="flex flex-col gap-2">
            {relatedCourses.map((course) => (
              <Link
                key={course.id}
                href={`/plan/courses/${course.id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] bg-warm-bg border border-border-light hover:border-gold/20 transition-all no-underline"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] font-semibold text-charcoal block truncate">
                    {course.title}
                  </span>
                  <span className="text-[12px] text-text-muted">
                    {course.provider_name}
                    {course.price_from != null && ` · From \u00A3${course.price_from.toLocaleString()}`}
                    {course.duration && ` · ${course.duration}`}
                  </span>
                </div>
                <div className="flex gap-1 shrink-0">
                  {course.qualifications.slice(0, 1).map((q) => (
                    <span
                      key={q}
                      className="bg-[#E8F5E9] text-success px-2 py-0.5 rounded-[5px] text-[10px] font-medium"
                    >
                      {findLabelById(QUALIFICATIONS_TREE, q) ?? q}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
          <Link
            href="/plan/courses"
            className="inline-block mt-3 text-[13px] font-semibold text-gold no-underline hover:text-gold-light transition-colors"
          >
            Browse all courses &rarr;
          </Link>
        </div>
      )}

      {/* Job alert signup for visitors only */}
      {!userRole && (
        <div className="mb-4">
          <JobAlertSignup signupNext={`/jobs/${listing.id}`} />
        </div>
      )}

      {/* ─── Message Modal ─── */}
      {showMessageModal && selectedWindowId && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMessageModal(false);
            }
          }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[4px]" />
          <div className="relative z-10 bg-white rounded-[16px] p-5 sm:p-7 w-full max-w-[520px] shadow-[0_24px_80px_rgba(0,0,0,0.15)]">
            <button
              onClick={() => setShowMessageModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
            >
              &times;
            </button>
            <h3 className="font-serif text-xl mb-1">
              Message {company.name}
            </h3>
            <p className="text-[13px] text-text-muted mb-5">
              Re: {listing.title}
            </p>
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              rows={4}
              placeholder="Introduce yourself and explain why you're interested..."
              className="w-full px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg resize-y outline-none focus:border-gold focus:bg-white placeholder:text-[#B0A99F] transition-all mb-4"
              maxLength={1000}
            />
            {error && (
              <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-2.5 mb-4 text-[13px] font-medium">
                {error}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleSendMessage}
                disabled={loading || !messageContent.trim()}
                className="px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-6 py-3 rounded-sm text-[14px] font-semibold text-text-muted bg-transparent border-[1.5px] border-border cursor-pointer font-sans transition-all hover:border-navy/30"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Window Picker Modal ─── */}
      {showWindowPicker && (
        <WindowPickerModal
          windows={sortedWindows}
          title="Which season?"
          description={
            windowPickerAction === "interest"
              ? "Choose which availability window to register interest with"
              : "Choose which season to message about"
          }
          onSelect={onWindowSelected}
          onClose={() => setShowWindowPicker(false)}
        />
      )}

      {/* ─── Sticky Mobile CTA ─── */}
      {!inlineCtaVisible &&
        userRole === "candidate" &&
        !isOwner &&
        !existingConv &&
        !messageSent &&
        candidateWindows.length > 0 &&
        listing.status === "active" && !jobHasEnded(listing) && (
          <div
            className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
            style={{
              background: "linear-gradient(transparent, #FAF8F4 30%)",
            }}
          >
            <div className="px-5 pb-7 pt-3">
              {listing.response_mode === "message" ? (
                <div className="flex gap-2 max-w-[600px] mx-auto">
                  <button
                    onClick={() => handleStartMessage()}
                    disabled={loading}
                    className="flex-1 py-3.5 rounded-[12px] bg-gold text-white text-[15px] font-semibold border-none cursor-pointer font-sans shadow-[0_4px_20px_rgba(196,151,90,0.3)] hover:bg-gold-light transition-colors"
                  >
                    Send a Message
                  </button>
                  {!interestRegistered && (
                    <button
                      onClick={() => handleInterest()}
                      disabled={loading}
                      className="px-4 py-3.5 rounded-[12px] bg-white text-navy text-[14px] font-semibold border-[1.5px] border-navy/18 cursor-pointer font-sans transition-all hover:border-navy shrink-0"
                    >
                      I&apos;m Interested
                    </button>
                  )}
                </div>
              ) : !interestRegistered ? (
                <button
                  onClick={() => handleInterest()}
                  disabled={loading}
                  className="w-full max-w-[600px] mx-auto block py-3.5 rounded-[12px] bg-gold text-white text-[15px] font-semibold border-none cursor-pointer font-sans shadow-[0_4px_20px_rgba(196,151,90,0.3)] hover:bg-gold-light transition-colors"
                >
                  I&apos;m Interested
                </button>
              ) : null}
            </div>
          </div>
        )}
      {!inlineCtaVisible && !userRole && listing.status === "active" && !jobHasEnded(listing) && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
          style={{
            background: "linear-gradient(transparent, #FAF8F4 30%)",
          }}
        >
          <div className="px-5 pb-7 pt-3">
            <Link
              href={`/signup?intent=work&next=${encodeURIComponent(`/jobs/${listing.id}`)}`}
              className="w-full max-w-[600px] mx-auto block text-center py-3.5 rounded-[12px] bg-gold text-white text-[15px] font-semibold no-underline font-sans shadow-[0_4px_20px_rgba(196,151,90,0.3)] hover:bg-gold-light transition-colors"
            >
              Sign Up to Express Interest
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
